import { Cart } from '@medusajs/medusa'
import { formatAmount, useCart } from 'medusa-react'
import React from 'react'
import { Promotion, StoreCart } from 'types/global'
import { Button } from '@chakra-ui/react'
import { medusaClient, medusaRequest } from '@lib/config'
import { AVOCADO_CONSTANT } from '@lib/constants'
import { handleError } from '@lib/util/handle-error'
import { toast } from 'react-toastify'
import { useAccount } from '@lib/context/account-context'
import { useQuery } from '@tanstack/react-query'

type CartTotalsProps = {
  cart: Omit<Cart, 'refundable_amount' | 'refunded_total'>
  isCheckout?: boolean
}

const CartTotals: React.FC<CartTotalsProps> = ({ cart, isCheckout }) => {
  const { setCart } = useCart()
  const { avocado, handleGetAvocadoUser } = useAccount()

  const { subtotal, gift_card_total, tax_total, shipping_total, total } =
    cart as StoreCart

  const countTotal =
    isCheckout && total !== undefined ? total : total! - (shipping_total ?? 0)

  const { data: discountDetail } = useQuery(
    [`cart-${cart.id}-discount-detail`, cart],
    async () =>
      await medusaRequest(
        'GET',
        `/store/carts/${cart?.id ?? ''}/detail-discount`
      )
  )

  const promotionsUsed: Promotion[] = discountDetail?.data?.promotionsUsed
  const discountsCodeUsed = discountDetail?.data?.discountsCodeUsed
  const campaignsUsed = discountDetail?.data?.campaignsUsed
  const totalDiscount = discountDetail?.data?.totalDiscount

  const getAmount = (amount: number | null | undefined) => {
    return formatAmount({
      amount: amount || 0,
      region: cart.region,
      includeTaxes: false
    })
  }

  const handleRemoveAvocadoUsed = async (multi_commit_id: string) => {
    try {
      const { data } = await medusaRequest(
        'POST',
        AVOCADO_CONSTANT.void_avocado,
        {
          multiCommitId: multi_commit_id,
          cartId: cart.id
        }
      )

      if (data.status !== 'fail') {
        const { cart: result } = await medusaClient.carts.retrieve(
          cart?.id ?? ''
        )

        setCart(result)

        await handleGetAvocadoUser(avocado?.token || '')
      } else {
        handleError(data.data.data)
        toast(data.data.data, {
          type: 'error'
        })
      }
    } catch (e) {
      toast((e as any).message, {
        type: 'error'
      })
    }
  }

  return (
    <div>
      <div className="text-gray-700 text-small-regular">
        <div className="flex items-center justify-between mb-2 text-gray-900 text-base-regular">
          <span>Subtotal</span>
          <span>{getAmount(subtotal)}</span>
        </div>
        <div className="flex flex-col gap-y-1">
          {!!totalDiscount && (
            <div>
              <div className="flex items-center justify-between">
                <span>Discount</span>
                <span>- {getAmount(totalDiscount)}</span>
              </div>
              <div className="flex flex-col">
                {promotionsUsed != null &&
                  promotionsUsed.map((promotion: any) => {
                    return promotion.total ? (
                      <div
                        className="flex items-center justify-between"
                        key={promotion.id}
                      >
                        <span className="pl-2">{promotion.description}</span>
                        <span>- {getAmount(promotion.total)}</span>
                      </div>
                    ) : null
                  })}

                {discountsCodeUsed != null &&
                  discountsCodeUsed.map((c: any) => {
                    return (
                      <div
                        className="flex items-center justify-between"
                        key={c.multi_commit_id}
                      >
                        <span className="pl-2">{c.code}</span>
                        <div>
                          <span>- {getAmount(c.total)}</span>
                        </div>
                      </div>
                    )
                  })}

                {campaignsUsed != null &&
                  campaignsUsed.map((c: any) => {
                    return (
                      <div
                        className="flex items-center justify-between"
                        key={c.multi_commit_id}
                      >
                        <span className="pl-2">{c.name}</span>
                        <div>
                          <span>- {getAmount(c.total)}</span>
                          <Button
                            variant="outline"
                            size="xs"
                            onClick={() => {
                              handleRemoveAvocadoUsed(c.multi_commit_id)
                            }}
                            className="ml-2"
                          >
                            remove
                          </Button>
                        </div>
                      </div>
                    )
                  })}
              </div>
            </div>
          )}
          {!!gift_card_total && (
            <div className="flex items-center justify-between">
              <span>Gift card</span>
              <span>- {getAmount(gift_card_total)}</span>
            </div>
          )}
          {isCheckout && (
            <div className="flex items-center justify-between">
              <span>Shipping</span>
              <span>
                {getAmount(cart.shipping_methods[0]?.price || shipping_total)}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span>Taxes</span>
            <span>{getAmount(tax_total)}</span>
          </div>
        </div>
        <div className="w-full h-px my-4 border-b border-gray-200 border-dashed" />
        <div className="flex items-center justify-between mb-2 text-gray-900 text-base-regular">
          <span>Total</span>
          <span>{getAmount(countTotal)}</span>
        </div>
      </div>
    </div>
  )
}

export default CartTotals
